import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoadingSpinner } from '../LoadingSpinner';
import { isDemoMode, simulateApiDelay, logDemoAction } from '../../config/demo';
import { 
  Search, 
  Filter, 
  Play, 
  Pause, 
  Volume2, 
  VolumeX,
  Share2,
  Heart,
  Bookmark,
  Clock,
  Users,
  Star,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  AlertTriangle,
  Info,
  Dumbbell,
  Target,
  Zap,
  Shield,
  Eye,
  EyeOff
} from 'lucide-react';

interface Exercise {
  id: string;
  name: string;
  description: string;
  category: 'strength' | 'cardio' | 'flexibility' | 'balance' | 'sports' | 'rehabilitation';
  subcategory: string;
  equipment: 'bodyweight' | 'dumbbells' | 'barbell' | 'kettlebell' | 'resistance_bands' | 'machine' | 'cable' | 'other';
  difficulty: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  muscleGroups: string[];
  primaryMuscles: string[];
  secondaryMuscles: string[];
  tags: string[];
  instructions: string[];
  cues: string[];
  commonMistakes: string[];
  safetyNotes: string[];
  variations: string[];
  progressions: string[];
  regressions: string[];
  // Video embedding data
  videoUrl?: string;
  videoSource?: 'youtube' | 'vimeo' | 'instagram' | 'tiktok' | 'custom';
  videoId?: string;
  videoChannel?: string;
  videoChannelUrl?: string;
  videoAttribution?: string;
  videoDuration?: number;
  videoThumbnail?: string;
  // Fallback media
  imageUrl?: string;
  gifUrl?: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  verified: boolean;
  popularity: number;
  rating: number;
  reviewCount: number;
}

const ExerciseLibraryPanel: React.FC = () => {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [filteredExercises, setFilteredExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedEquipment, setSelectedEquipment] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [expandedExercise, setExpandedExercise] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState<{ [key: string]: boolean }>({});
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [bookmarks, setBookmarks] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadExercises();
  }, []);

  useEffect(() => {
    filterExercises();
  }, [exercises, searchQuery, selectedCategory, selectedEquipment, selectedDifficulty]);

  const loadExercises = async () => {
    try {
      setLoading(true);
      
      // Simulate API delay
      await simulateApiDelay();
      
      if (isDemoMode()) {
        logDemoAction('Exercise library loaded', {
          component: 'ExerciseLibraryPanel',
          timestamp: new Date().toISOString()
        });
      }
      
      // Mock data - replace with actual API call
      const mockExercises: Exercise[] = [
        {
          id: 'ex_1',
          name: 'Push-up',
          description: 'A classic bodyweight exercise that targets the chest, shoulders, and triceps.',
          category: 'strength',
          subcategory: 'upper_body',
          equipment: 'bodyweight',
          difficulty: 'beginner',
          muscleGroups: ['chest', 'shoulders', 'triceps'],
          primaryMuscles: ['pectorals', 'anterior_deltoids', 'triceps'],
          secondaryMuscles: ['core', 'serratus_anterior'],
          tags: ['bodyweight', 'chest', 'beginner', 'home_workout'],
          instructions: [
            'Start in a plank position with hands slightly wider than shoulders',
            'Lower your body until chest nearly touches the floor',
            'Push back up to starting position',
            'Keep core tight throughout the movement'
          ],
          cues: [
            'Keep body in straight line',
            'Engage core',
            'Full range of motion'
          ],
          commonMistakes: [
            'Sagging hips',
            'Incomplete range of motion',
            'Flaring elbows too wide'
          ],
          safetyNotes: [
            'Keep wrists aligned under shoulders',
            'Stop if you feel wrist pain'
          ],
          variations: ['incline_pushup', 'decline_pushup', 'diamond_pushup'],
          progressions: ['one_arm_pushup', 'archer_pushup'],
          regressions: ['knee_pushup', 'wall_pushup'],
          videoUrl: 'https://www.youtube.com/watch?v=4uQtPyWkXcc',
          videoSource: 'youtube',
          videoId: '4uQtPyWkXcc',
          videoChannel: 'Athlean-X',
          videoChannelUrl: 'https://www.youtube.com/@athleanx',
          videoAttribution: 'Video provided by Athlean-X — used for educational purposes only',
          videoDuration: 180,
          videoThumbnail: 'https://img.youtube.com/vi/4uQtPyWkXcc/maxresdefault.jpg',
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: 'system',
          verified: true,
          popularity: 95,
          rating: 4.8,
          reviewCount: 1250
        },
        {
          id: 'ex_2',
          name: 'Goblet Squat',
          description: 'A beginner-friendly squat variation using a dumbbell or kettlebell.',
          category: 'strength',
          subcategory: 'lower_body',
          equipment: 'dumbbells',
          difficulty: 'beginner',
          muscleGroups: ['glutes', 'quads', 'hamstrings'],
          primaryMuscles: ['gluteus_maximus', 'quadriceps'],
          secondaryMuscles: ['hamstrings', 'core', 'calves'],
          tags: ['squat', 'glutes', 'quads', 'beginner', 'dumbbell'],
          instructions: [
            'Hold a dumbbell or kettlebell at chest level',
            'Stand with feet shoulder-width apart',
            'Lower down into a squat position',
            'Keep chest up and core engaged',
            'Return to standing position'
          ],
          cues: [
            'Chest up',
            'Knees track over toes',
            'Full depth',
            'Drive through heels'
          ],
          commonMistakes: [
            'Knees caving in',
            'Leaning too far forward',
            'Not reaching full depth'
          ],
          safetyNotes: [
            'Keep weight close to body',
            'Maintain neutral spine',
            'Stop if you feel knee pain'
          ],
          variations: ['bodyweight_squat', 'front_squat', 'overhead_squat'],
          progressions: ['front_squat', 'back_squat'],
          regressions: ['bodyweight_squat', 'assisted_squat'],
          videoUrl: 'https://www.youtube.com/watch?v=4uQtPyWkXcc',
          videoSource: 'youtube',
          videoId: '4uQtPyWkXcc',
          videoChannel: 'Fitness Blender',
          videoChannelUrl: 'https://www.youtube.com/@fitnessblender',
          videoAttribution: 'Video provided by Fitness Blender — used for educational purposes only',
          videoDuration: 120,
          videoThumbnail: 'https://img.youtube.com/vi/4uQtPyWkXcc/maxresdefault.jpg',
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: 'system',
          verified: true,
          popularity: 88,
          rating: 4.6,
          reviewCount: 890
        }
      ];

      setExercises(mockExercises);
    } catch (error) {
      console.error('Error loading exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterExercises = () => {
    let filtered = exercises;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(exercise =>
        exercise.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exercise.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exercise.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) ||
        exercise.muscleGroups.some(muscle => muscle.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(exercise => exercise.category === selectedCategory);
    }

    // Equipment filter
    if (selectedEquipment !== 'all') {
      filtered = filtered.filter(exercise => exercise.equipment === selectedEquipment);
    }

    // Difficulty filter
    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(exercise => exercise.difficulty === selectedDifficulty);
    }

    setFilteredExercises(filtered);
  };

  const toggleVideo = (exerciseId: string) => {
    setShowVideo(prev => ({
      ...prev,
      [exerciseId]: !prev[exerciseId]
    }));
  };

  const toggleFavorite = (exerciseId: string) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(exerciseId)) {
        newFavorites.delete(exerciseId);
      } else {
        newFavorites.add(exerciseId);
      }
      return newFavorites;
    });
  };

  const toggleBookmark = (exerciseId: string) => {
    setBookmarks(prev => {
      const newBookmarks = new Set(prev);
      if (newBookmarks.has(exerciseId)) {
        newBookmarks.delete(exerciseId);
      } else {
        newBookmarks.add(exerciseId);
      }
      return newBookmarks;
    });
  };

  const shareExercise = async (exercise: Exercise) => {
    if (isDemoMode()) {
      logDemoAction('Exercise shared', {
        exerciseId: exercise.id,
        exerciseName: exercise.name,
        timestamp: new Date().toISOString()
      });
      alert('Exercise shared! (Demo Mode)');
      return;
    }

    const shareData = {
      title: exercise.name,
      text: exercise.description,
      url: exercise.videoUrl || window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      const shareText = `${exercise.name}\n${exercise.description}\n${exercise.videoUrl || ''}`;
      await navigator.clipboard.writeText(shareText);
      alert('Exercise details copied to clipboard!');
    }
  };

  const getDifficultyColor = (difficulty: string): string => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-orange-100 text-orange-800';
      case 'expert': return 'bg-red-100 text-red-800';
      default: return 'text-gray-800';
    }
  };

  const getDifficultyBackgroundStyle = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return { background: 'var(--color-success-light)', color: 'var(--color-success-dark)' };
      case 'intermediate': return { background: 'var(--color-warning-light)', color: 'var(--color-warning-dark)' };
      case 'advanced': return { background: 'var(--color-error-light)', color: 'var(--color-error-dark)' };
      case 'expert': return { background: 'var(--color-error-light)', color: 'var(--color-error-dark)' };
      default: return { background: 'var(--color-background-secondary)', color: 'var(--color-text)' };
    }
  };

  const getEquipmentIcon = (equipment: string) => {
    switch (equipment) {
      case 'bodyweight': return <Users className="w-4 h-4" />;
      case 'dumbbells': return <Dumbbell className="w-4 h-4" />;
      case 'barbell': return <Dumbbell className="w-4 h-4" />;
      case 'kettlebell': return <Dumbbell className="w-4 h-4" />;
      case 'resistance_bands': return <Zap className="w-4 h-4" />;
      case 'machine': return <Target className="w-4 h-4" />;
      case 'cable': return <Target className="w-4 h-4" />;
      default: return <Dumbbell className="w-4 h-4" />;
    }
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const generateEmbedUrl = (videoId: string, source: string): string => {
    switch (source) {
      case 'youtube':
        return `https://www.youtube.com/embed/${videoId}`;
      case 'vimeo':
        return `https://player.vimeo.com/video/${videoId}`;
      case 'instagram':
        return `https://www.instagram.com/p/${videoId}/embed/`;
      case 'tiktok':
        return `https://www.tiktok.com/embed/v2/${videoId}`;
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" variant="fitness" text="Loading exercise library..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={{ 
            color: 'var(--color-text)',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>Exercise Library</h2>
          <p className="text-base" style={{ 
            color: 'var(--color-text-muted)',
            lineHeight: '1.6'
          }}>Browse exercises with video demonstrations</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold" style={{ 
            color: 'var(--color-primary)',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>{filteredExercises.length}</div>
          <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Exercises</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="sculptd-card-elevated sculptd-sheen" style={{
        boxShadow: 'var(--shadow-card-separation)'
      }}>
        <div className="p-6">
          <div className="space-y-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
              <Input
                placeholder="Search exercises..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:border-transparent"
                  style={{ 
                    borderColor: 'var(--color-border)', 
                    background: 'white',
                    color: 'var(--color-text)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(58, 175, 169, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-border)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="all">All Categories</option>
                  <option value="strength">Strength</option>
                  <option value="cardio">Cardio</option>
                  <option value="flexibility">Flexibility</option>
                  <option value="balance">Balance</option>
                  <option value="sports">Sports</option>
                  <option value="rehabilitation">Rehabilitation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Equipment</label>
                <select
                  value={selectedEquipment}
                  onChange={(e) => setSelectedEquipment(e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:border-transparent"
                  style={{ 
                    borderColor: 'var(--color-border)', 
                    background: 'white',
                    color: 'var(--color-text)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(58, 175, 169, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-border)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="all">All Equipment</option>
                  <option value="bodyweight">Bodyweight</option>
                  <option value="dumbbells">Dumbbells</option>
                  <option value="barbell">Barbell</option>
                  <option value="kettlebell">Kettlebell</option>
                  <option value="resistance_bands">Resistance Bands</option>
                  <option value="machine">Machine</option>
                  <option value="cable">Cable</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Difficulty</label>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:border-transparent"
                  style={{ 
                    borderColor: 'var(--color-border)', 
                    background: 'white',
                    color: 'var(--color-text)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(58, 175, 169, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-border)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="all">All Levels</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="expert">Expert</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Exercise Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredExercises.map((exercise) => (
          <div key={exercise.id} className="sculptd-card-elevated sculptd-sheen hover:shadow-lg transition-shadow" style={{
            boxShadow: 'var(--shadow-card-separation)',
            border: '1px solid rgba(0, 0, 0, 0.08)'
          }}
          onMouseEnter={(e) => (e.target as HTMLElement).style.boxShadow = 'var(--shadow-card-hover)'}
          onMouseLeave={(e) => (e.target as HTMLElement).style.boxShadow = 'var(--shadow-card-separation)'}>
            <div className="sculptd-container-padding">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold" style={{ 
                    color: 'var(--color-text)',
                    textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}>{exercise.name}</h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>{exercise.description}</p>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleFavorite(exercise.id)}
                    style={{ color: favorites.has(exercise.id) ? 'var(--color-error)' : 'var(--color-text-muted)' }}
                  >
                    <Heart className={`w-4 h-4 ${favorites.has(exercise.id) ? 'fill-current' : ''}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleBookmark(exercise.id)}
                    className={bookmarks.has(exercise.id) ? '' : ''}
                    style={{ color: bookmarks.has(exercise.id) ? 'var(--color-primary)' : 'var(--color-text-muted)' }}
                  >
                    <Bookmark className={`w-4 h-4 ${bookmarks.has(exercise.id) ? 'fill-current' : ''}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => shareExercise(exercise)}
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="sculptd-container-padding space-y-6">
              {/* Video Section */}
              {exercise.videoUrl && (
                <div className="space-y-2">
                  <div className="relative">
                    {showVideo[exercise.id] ? (
                      <div className="relative w-full h-48 bg-black rounded-lg overflow-hidden">
                        <iframe
                          src={generateEmbedUrl(exercise.videoId!, exercise.videoSource!)}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={exercise.name}
                        />
                      </div>
                    ) : (
                      <div className="relative w-full h-48 rounded-lg overflow-hidden cursor-pointer group"
                           style={{ background: 'var(--color-background-secondary)' }}
                           onClick={() => toggleVideo(exercise.id)}>
                        <img
                          src={exercise.videoThumbnail}
                          alt={exercise.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center group-hover:bg-opacity-40 transition-all">
                          <div className="bg-white bg-opacity-90 rounded-full p-3">
                            <Play className="w-6 h-6" style={{ color: 'var(--color-text)' }} />
                          </div>
                        </div>
                        {exercise.videoDuration && (
                          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                            {formatDuration(exercise.videoDuration)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Video Attribution */}
                  <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    <div dangerouslySetInnerHTML={{ __html: exercise.videoAttribution || '' }} />
                    <div className="mt-1 flex items-center space-x-1">
                      <AlertTriangle className="w-3 h-3" />
                      <span>Always consult your physician before beginning any exercise program. Technique videos are for reference only.</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Exercise Details */}
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                    background: exercise.difficulty === 'beginner' ? 'var(--color-success)' : 
                               exercise.difficulty === 'intermediate' ? 'var(--color-warning)' : 'var(--color-error)', 
                    color: 'var(--color-white)' 
                  }}>
                    {exercise.difficulty}
                  </span>
                  <div className="flex items-center space-x-1" style={{ color: 'var(--color-text-muted)' }}>
                    {getEquipmentIcon(exercise.equipment)}
                    <span className="text-sm capitalize">{exercise.equipment.replace('_', ' ')}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4" style={{ color: 'var(--color-warning)' }} />
                    <span>{exercise.rating.toFixed(1)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>{exercise.reviewCount}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1">
                  {exercise.muscleGroups.slice(0, 3).map((muscle) => (
                    <span key={muscle} className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                      background: 'var(--color-secondary)', 
                      color: 'var(--color-white)' 
                    }}>
                      {muscle}
                    </span>
                  ))}
                  {exercise.muscleGroups.length > 3 && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                      background: 'var(--color-secondary)', 
                      color: 'var(--color-white)' 
                    }}>
                      +{exercise.muscleGroups.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              {/* Expandable Details */}
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setExpandedExercise(
                    expandedExercise === exercise.id ? null : exercise.id
                  )}
                  className="w-full"
                >
                  {expandedExercise === exercise.id ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Show Details
                    </>
                  )}
                </Button>

                {expandedExercise === exercise.id && (
                  <div className="space-y-3 pt-2 border-t">
                    {/* Instructions */}
                    <div>
                      <h4 className="font-medium text-sm mb-2">Instructions</h4>
                      <ol className="text-sm space-y-1" style={{ color: 'var(--color-text-muted)' }}>
                        {exercise.instructions.map((instruction, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <span className="font-medium" style={{ color: 'var(--color-primary)' }}>{index + 1}.</span>
                            <span>{instruction}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    {/* Cues */}
                    <div>
                      <h4 className="font-medium text-sm mb-2">Key Cues</h4>
                      <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-muted)' }}>
                        {exercise.cues.map((cue, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <span style={{ color: 'var(--color-success)' }}>•</span>
                            <span>{cue}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Safety Notes */}
                    <div>
                      <h4 className="font-medium text-sm mb-2 flex items-center space-x-1">
                        <Shield className="w-4 h-4" style={{ color: 'var(--color-error)' }} />
                        <span>Safety Notes</span>
                      </h4>
                      <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-muted)' }}>
                        {exercise.safetyNotes.map((note, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <span style={{ color: 'var(--color-error)' }}>⚠</span>
                            <span>{note}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Variations */}
                    {(exercise.variations.length > 0 || exercise.progressions.length > 0 || exercise.regressions.length > 0) && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">Related Exercises</h4>
                        <div className="space-y-2">
                          {exercise.progressions.length > 0 && (
                            <div>
                              <span className="text-xs font-medium" style={{ color: 'var(--color-success)' }}>Progressions:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {exercise.progressions.map((progression) => (
                                  <span key={progression} className="px-2 py-1 rounded-full text-xs font-medium border" style={{ 
                                    background: 'transparent', 
                                    color: 'var(--color-primary)',
                                    borderColor: 'var(--color-primary)'
                                  }}>
                                    {progression.replace('_', ' ')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {exercise.regressions.length > 0 && (
                            <div>
                              <span className="text-xs font-medium" style={{ color: 'var(--color-primary)' }}>Regressions:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {exercise.regressions.map((regression) => (
                                  <span key={regression} className="px-2 py-1 rounded-full text-xs font-medium border" style={{ 
                                    background: 'transparent', 
                                    color: 'var(--color-secondary)',
                                    borderColor: 'var(--color-secondary)'
                                  }}>
                                    {regression.replace('_', ' ')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {exercise.variations.length > 0 && (
                            <div>
                              <span className="text-xs font-medium" style={{ color: 'var(--color-secondary)' }}>Variations:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {exercise.variations.map((variation) => (
                                  <span key={variation} className="px-2 py-1 rounded-full text-xs font-medium border" style={{ 
                                    background: 'transparent', 
                                    color: 'var(--color-accent)',
                                    borderColor: 'var(--color-accent)'
                                  }}>
                                    {variation.replace('_', ' ')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredExercises.length === 0 && (
        <div className="sculptd-card-elevated sculptd-sheen" style={{
          boxShadow: 'var(--shadow-card-separation)',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}>
          <div className="sculptd-container-padding text-center">
            <div style={{ color: 'var(--color-text-muted)' }}>
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--color-text)' }}>No exercises found</h3>
              <p>Try adjusting your search criteria or filters</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseLibraryPanel;
